import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { Clock, User, FileText, Calendar, MapPin, X } from 'lucide-react';

interface TimelineEntry {
  id: string;
  titulo: string;
  descricao: string | null;
  tipo: string;
  criado_por: string | null;
  created_at: string;
  metadata: any;
}

interface PropertyInterest {
  property_id: string;
  property_title: string;
  created_at: string;
  status: string;
}

interface LeadTimelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: string;
  leadName: string;
}

export function LeadTimelineModal({ isOpen, onClose, leadId, leadName }: LeadTimelineModalProps) {
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [properties, setProperties] = useState<PropertyInterest[]>([]);
  const [loading, setLoading] = useState(false);
  const [userNames, setUserNames] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (isOpen && leadId) {
      loadTimeline();
      loadPropertyInterests();
    }
  }, [isOpen, leadId]);

  // Função para buscar nome do usuário por ID
  const getUserName = async (userId: string): Promise<string> => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('user_id', userId)
        .single();
      
      if (profile) {
        return `${profile.first_name} ${profile.last_name}`.trim();
      }
    } catch (error) {
      console.error('Erro ao buscar nome do usuário:', error);
    }
    return 'Usuário';
  };

  // Função para processar nomes de usuários na timeline
  const processUserNames = async (timelineData: TimelineEntry[]) => {
    const userNamesMap: {[key: string]: string} = {};
    
    // Buscar nomes únicos de usuários
    const uniqueUserIds = new Set<string>();
    timelineData.forEach(entry => {
      if (entry.criado_por && entry.criado_por !== 'Sistema' && entry.criado_por !== 'IA do Sistema') {
        // Se criado_por parece ser um UUID, vamos buscar o nome
        if (entry.criado_por.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          uniqueUserIds.add(entry.criado_por);
        }
      }
    });

    // Buscar nomes para todos os user IDs únicos
    for (const userId of uniqueUserIds) {
      userNamesMap[userId] = await getUserName(userId);
    }

    setUserNames(userNamesMap);
  };

  const loadTimeline = async () => {
    setLoading(true);
    console.log('🔍 Loading timeline for leadId:', leadId);
    try {
      // 1) Tentar timeline diretamente pelo id atual (leads_all)
      const { data: tlDirect, error } = await supabase
        .from('timeline_leads')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Timeline error:', error);
        throw error;
      }

      let timelineData = tlDirect || [];

      // 2) Se vazio, buscar email/telefone do lead e tentar via leads_portais
      if (timelineData.length === 0) {
        const { data: leadData } = await supabase
          .from('leads_all')
          .select('name, source, created_at, email, phone')
          .eq('id', leadId)
          .maybeSingle();

        const portalIds = new Set<string>();
        if (leadData?.email) {
          const { data: lpEmail } = await supabase
            .from('leads_portais')
            .select('id')
            .eq('email', leadData.email)
            .order('created_at', { ascending: false })
            .limit(20);
          lpEmail?.forEach(r => portalIds.add(r.id));
        }
        if (leadData?.phone) {
          const { data: lpPhone } = await supabase
            .from('leads_portais')
            .select('id')
            .eq('phone', leadData.phone)
            .order('created_at', { ascending: false })
            .limit(20);
          lpPhone?.forEach(r => portalIds.add(r.id));
        }

        if (portalIds.size > 0) {
          const { data: tlPortal } = await supabase
            .from('timeline_leads')
            .select('*')
            .in('lead_id', Array.from(portalIds))
            .order('created_at', { ascending: false });
          if (tlPortal && tlPortal.length > 0) {
            timelineData = tlPortal;
          } else if (leadData) {
            // 3) Fallback: criar entrada
            timelineData = [{
              id: 'lead-creation',
              lead_id: leadId,
              titulo: 'Lead criado',
              descricao: `Lead "${leadData.name}" foi criado via ${leadData.source}`,
              tipo: 'criacao',
              criado_por: 'Sistema',
              created_at: leadData.created_at,
              metadata: { source: leadData.source }
            }];
          }
        } else if (leadData) {
          timelineData = [{
            id: 'lead-creation',
            lead_id: leadId,
            titulo: 'Lead criado',
            descricao: `Lead "${leadData.name}" foi criado via ${leadData.source}`,
            tipo: 'criacao',
            criado_por: 'Sistema',
            created_at: leadData.created_at,
            metadata: { source: leadData.source }
          }];
        }
      }

      // Deduplicate timeline entries
      const seen = new Set<string>();
      const uniqueTimeline = timelineData.filter(entry => {
        const key = `${entry.titulo}-${entry.created_at?.substring(0, 16)}`; // same to minute
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setTimeline(uniqueTimeline);
      await processUserNames(uniqueTimeline);
    } catch (error) {
      console.error('Erro ao carregar timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPropertyInterests = async () => {
    try {
      // Buscar dados do lead atual (inclui email/telefone para fallback)
      const { data: leadData, error: leadError } = await supabase
        .from('leads_all')
        .select('property_listing_id, source, created_at, email, phone')
        .eq('id', leadId)
        .maybeSingle();

      if (leadError) {
        console.error('Erro ao buscar dados do lead:', leadError);
        return;
      }

      // Buscar imóveis de interesse via timeline direto
      const { data: timelineData } = await supabase
        .from('timeline_leads')
        .select('metadata, created_at')
        .eq('lead_id', leadId)
        .not('metadata->>property_id', 'is', null);

      const propertyIds = new Set<string>();
      const interests: PropertyInterest[] = [];

      // 1) Imóvel do lead atual
      const currentPropertyId = leadData?.property_listing_id as string | undefined;
      if (currentPropertyId && !propertyIds.has(currentPropertyId)) {
        propertyIds.add(currentPropertyId);
        const { data: propertyData } = await supabase
          .from('properties')
          .select('title')
          .eq('listing_id', currentPropertyId)
          .maybeSingle();
        interests.push({
          property_id: currentPropertyId,
          property_title: propertyData?.title || currentPropertyId,
          created_at: leadData!.created_at,
          status: 'interesse'
        });
      }

      // 2) Imóveis presentes na timeline deste lead
      for (const entry of timelineData || []) {
        const md = entry.metadata as any;
        const pid = md?.property_id as string | undefined;
        if (pid && !propertyIds.has(pid)) {
          propertyIds.add(pid);
          interests.push({
            property_id: pid,
            property_title: md?.property_title || pid,
            created_at: entry.created_at,
            status: 'interesse'
          });
        }
      }

      // 3) Fallback por leads_portais (email/telefone)
      const portalRows: any[] = [];
      if (leadData?.email) {
        const { data: lpEmail } = await supabase
          .from('leads_portais')
          .select('client_listing_id, origin_listing_id, portal, created_at')
          .eq('email', leadData.email)
          .order('created_at', { ascending: false })
          .limit(20);
        if (lpEmail) portalRows.push(...lpEmail);
      }
      if (leadData?.phone) {
        const { data: lpPhone } = await supabase
          .from('leads_portais')
          .select('client_listing_id, origin_listing_id, portal, created_at')
          .eq('phone', leadData.phone)
          .order('created_at', { ascending: false })
          .limit(20);
        if (lpPhone) portalRows.push(...lpPhone);
      }

      const listingIds: string[] = [];
      for (const r of portalRows) {
        const pid = r.client_listing_id || r.origin_listing_id;
        if (pid && !propertyIds.has(pid)) {
          propertyIds.add(pid);
          listingIds.push(pid);
        }
      }

      if (listingIds.length > 0) {
        const { data: propsBatch } = await supabase
          .from('properties')
          .select('listing_id, title')
          .in('listing_id', listingIds);
        const propsMap = new Map(propsBatch?.map(p => [p.listing_id, p]) || []);

        for (const r of portalRows) {
          const pid = r.client_listing_id || r.origin_listing_id;
          if (!pid) continue;
          const p = propsMap.get(pid);
          interests.push({
            property_id: pid,
            property_title: p?.title || pid,
            created_at: r.created_at,
            status: 'interesse'
          });
        }
      }

      // Deduplicate property interests
      const uniqueInterests = interests.filter((interest, index, arr) => 
        arr.findIndex(i => i.property_id === interest.property_id) === index
      );

      setProperties(uniqueInterests);
    } catch (error) {
      console.error('Erro ao carregar imóveis de interesse:', error);
    }
  };

  const getTimelineIcon = (tipo: string) => {
    switch (tipo) {
      case 'criacao':
        return <User className="w-4 h-4" />;
      case 'status_change':
        return <Calendar className="w-4 h-4" />;
      case 'novo_interesse':
      case 'interesse_portal':
        return <MapPin className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getTimelineColor = (tipo: string) => {
    switch (tipo) {
      case 'criacao':
        return 'bg-green-100 text-green-800';
      case 'status_change':
        return 'bg-blue-100 text-blue-800';
      case 'novo_interesse':
      case 'interesse_portal':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const createPropertyLink = (propertyId: string, title: string) => {
    return (
      <a
        href={`/imovel/${propertyId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
      >
        {title}
      </a>
    );
  };

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${isOpen ? 'block' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Timeline - {leadName}</h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">

            <div className="space-y-6">
              {/* Imóveis de Interesse */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Imóveis de Interesse
                </h3>
                {properties.length === 0 ? (
                  <p className="text-gray-500">Nenhum imóvel de interesse registrado.</p>
                ) : (
                  <div className="space-y-2">
                    {properties.map((property, index) => (
                       <div key={index} className="bg-white p-3 rounded border flex justify-between items-center">
                         <div>
                           {createPropertyLink(property.property_id, property.property_title)}
                           <p className="text-sm text-gray-500">
                             Interesse demonstrado em {formatDate(property.created_at)}
                           </p>
                         </div>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {property.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div>
                <h3 className="font-semibold mb-3">Histórico de Atividades</h3>
                {loading ? (
                  <p className="text-gray-500">Carregando timeline...</p>
                ) : timeline.length === 0 ? (
                  <p className="text-gray-500">Nenhuma atividade registrada.</p>
                ) : (
                  <div className="space-y-4">
                    {timeline.map((entry) => (
                      <div key={entry.id} className="border-l-4 border-blue-500 pl-4 pb-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-full ${getTimelineColor(entry.tipo)}`}>
                            {getTimelineIcon(entry.tipo)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium">{entry.titulo}</h4>
                              <span className="text-sm text-gray-500">
                                {formatDate(entry.created_at)}
                              </span>
                            </div>
                            {entry.descricao && (
                              <p className="text-gray-600 mt-1">{entry.descricao}</p>
                            )}
                            {entry.criado_por && (
                              <p className="text-xs text-gray-500 mt-1">
                                Por: {userNames[entry.criado_por] || entry.criado_por}
                              </p>
                            )}
                            {(entry.metadata as any)?.property_title && (
                              <div className="mt-2">
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  Imóvel: {(entry.metadata as any).property_title}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}